'use client';

import { Button } from "@/components/ui/Button";
import { getLanguageObjectByName, LANGUAGES, NATIVE_LANGUAGES } from "@/lib/constants";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Card, CardBody } from "@heroui/card";
import { Image } from "@heroui/image";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PLAUSIBLE_EVENTS, trackPlausibleEvent } from "@/lib/client-stats";
import LoginModal from "@/app/(static)/LoginModal";
import { useRouter } from "next/navigation";
import { RouteNames } from "@/lib/constants-ui";

interface JoinActionSectionTranslations {
    title: string;
    actionButton: string;
    actionButtonInfo: string;
    features: {
        feature1: string;
        feature2: string;
        feature3: string;
        feature4: string;
    }
}

function getTranslations(language: string): JoinActionSectionTranslations {
    if (language === "english") {
        return {
            title: "Do you like it? Want more?",
            actionButton: "Get started",
            actionButtonInfo: "Free & quick registration, no subscriptions!",
            features: {
                feature1: "For expats and alike – unlike any other language app you've tried!",
                feature2: "Learn by scrolling and playing!",
                feature3: "Real grammar and idioms to sound like locals!",
                feature4: "Endless FYP feed for language learning",
            }
        };
    } else if (language === "spanish") {
        return {
            title: "¿Te gusta? ¿Quieres más?",
            actionButton: "Comenzar",
            actionButtonInfo: "Registro gratuito y rápido, sin suscripciones!",
            features: {
                feature1: "Para inmigrantes – diferente a cualquier otra app que has intentado!",
                feature2: "Aprende leyendo y jugando!",
                feature3: "Gramática real y refranes para sonar como locales!",
                feature4: "Feed de FYP infinito para aprender idiomas",
            }
        };
    } else if (language == "russian") {
        return {    
            title: "Вам нравится? Хотите больше?",
            actionButton: "Начать",
            actionButtonInfo: "Бесплатная и быстрая регистрация, без подписок!",
            features: {
                feature1: "Для иммигрантов – такого приложения вы еще не видели!",
                feature2: "Учиться играя, читая и листая!",
                feature3: "Реальная грамматика и фразеологизмы для звучания как местные!",
                feature4: "Бесконечная лента FYP для изучения языков",
            }
        };
    } 
}

function EmojiCloudAnimation() {
    const emojis = ['🤝', '📕', '💪'];

    return <div></div>
}

export function JoinActionSection({locale, learningLanguage, className}: {locale: string, learningLanguage: string, className?: string}) {
    const languageObject = useMemo(() => getLanguageObjectByName(locale) || NATIVE_LANGUAGES.english, [locale]);
    const learningLanguageObject = useMemo(() => getLanguageObjectByName(learningLanguage) || LANGUAGES.english, [learningLanguage]);

    const router = useRouter();

    const t = getTranslations(locale);

    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const closeLogin = () => setIsLoginOpen(false);

    const handleActionButtonClick = () => {
        // setIsLoginOpen(true);
        trackPlausibleEvent({name: PLAUSIBLE_EVENTS.seoPostCTAButtonTap, onlyOnce: true});
        router.push(RouteNames.welcomeTryIt);
    };

    return (
        <div className={`flex flex-col items-center justify-center mx-auto ${className}`}>
            <Card className="w-full rounded-b-none">
                <CardBody className="pb-0">
                    {/* Mobile version */}
                    <div className="w-full block">
                        <div className="flex flex-row items-stretch justify-between gap-2">
                            <div className="w-48 self-end">     
                                <EmojiCloudAnimation />
                                <Image 
                                    src="/images/avatar_chatlanga_inviting_white_md.jpg"
                                    alt="Join Action Section"
                                    className="w-48"                            
                                />
                            </div>

                            <div className="pb-4 h-48 flex flex-col items-center justify-center">
                                <div className="text-lg/5 tracking-tight text-center font-semibold mb-4">{t.title}</div>
                                <Button color="success" variant="shadow" size="md" className="font-bold mb-2" startContent={<span className="text-lg">{learningLanguageObject.emoji}</span>} onPress={handleActionButtonClick}>{t.actionButton}</Button>                                
                                <div className="text-xs/4 text-gray-500 tracking-tight text-center">{t.actionButtonInfo}</div>
                            </div>
                        </div>
                    </div>        
                </CardBody>
            </Card>            

            <div className="w-full mt-0">
                <Card className="w-full rounded-t-none border-2 border-t-0 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600">
                    <CardBody className="w-full p-6">
                        <div className="flex flex-col gap-2 text-md/4 text-white">
                            <div className="flex flex-row items-center gap-3">
                                <div className="w-6 text-2xl">🤗</div>
                                <div>{t.features.feature1}</div>
                            </div>
                            <div className="border-b border-white/20 my-0.5 mx-auto w-2/3"></div>
                            <div className="flex flex-row items-center gap-3">
                                <div className="w-6 text-2xl">🎮</div>
                                <div>{t.features.feature2}</div>
                            </div>
                            <div className="border-b border-white/20 my-0.5 mx-auto w-2/3"></div>
                            <div className="flex flex-row items-center gap-3">
                                <div className="w-6 text-2xl">💪</div>
                                <div>{t.features.feature3}</div>
                            </div>
                            <div className="border-b border-white/20 my-0.5 mx-auto w-2/3"></div>
                            <div className="relative h-8 overflow-hidden">
                                <motion.div
                                    className="flex flex-col gap-4"
                                    animate={{
                                        y: [0, -48]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "linear"
                                    }}
                                >
                                    <div className="flex flex-row items-center gap-3 h-8">
                                        <div className="w-6 text-2xl">🧠</div>
                                        <div>{t.features.feature4}</div>
                                    </div>
                                    <div className="flex flex-row items-center gap-3 h-8">
                                        <div className="w-6 text-2xl">🧠</div>
                                        <div>{t.features.feature4}</div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <LoginModal isOpen={isLoginOpen} onClose={closeLogin} locale={languageObject?.name || 'english'} />
        </div>
    );
}